(function (factory) {
  if (typeof module === 'object' && module.exports) module.exports = factory;
  else {
    const start = () => factory(document, window);
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, { once: true });
    else start();
  }
})(function mountDeferredDemos(document, view) {
  'use strict';
  const config = document.getElementById('homepage-demo-assets');
  if (!config || config.dataset.ready) return;
  const entries = JSON.parse(config.textContent).map(entry => ({
    ...entry, root: document.querySelector(entry.selector), state: 'idle', activation: null, edits: new Map()
  })).filter(entry => entry.root);
  config.dataset.ready = 'true';
  const scripts = new Map();
  let observer, pageActive = true;

  function script(src) {
    if (scripts.has(src)) return scripts.get(src);
    const pending = new Promise((resolve, reject) => {
      const element = document.createElement('script');
      element.src = src;
      element.async = false;
      element.onload = () => { element.onload = element.onerror = null; resolve(); };
      element.onerror = () => {
        element.remove();
        scripts.delete(src);
        reject(new Error('Could not load the demo.'));
      };
      document.head.appendChild(element);
    });
    scripts.set(src, pending);
    return pending;
  }

  function load(entry) {
    if (!entry || entry.state !== 'idle') return;
    entry.state = 'loading';
    entry.root.setAttribute('aria-busy', 'true');
    entry.root.dataset.demoLoading = 'true';
    // Dependencies must execute before their controller mounts the rendered UI.
    entry.pending = entry.scripts.reduce((chain, src) => chain.then(() => script(src)), Promise.resolve())
      .then(() => {
        entry.state = 'ready';
        entry.root.removeAttribute('aria-busy');
        delete entry.root.dataset.demoLoading;
        // Native fields can be edited during a slow download. Apply those edits
        // through the mounted controller after it restores its saved draft.
        entry.edits.forEach((edit, field) => {
          if (!field.isConnected) return;
          if (field.type === 'checkbox') field.checked = edit.checked;
          else field.value = edit.value;
          field.dispatchEvent(new view.Event('input', { bubbles: true }));
          if (document.activeElement === field && edit.start !== null) field.setSelectionRange(edit.start, edit.end);
        });
        entry.edits.clear();
        if (observer) observer.unobserve(entry.root);
        if (entries.every(item => item.state === 'ready')) cleanup();
        const activation = entry.activation;
        entry.activation = null;
        if (pageActive && activation && activation.isConnected) {
          activation.focus({ preventScroll: true });
          activation.click();
        }
      })
      .catch(() => {
        // A transient failure remains retryable on the next visit or interaction.
        entry.state = 'idle';
        entry.activation = null;
        entry.root.removeAttribute('aria-busy');
        delete entry.root.dataset.demoLoading;
      });
  }

  function loadAnchor(hash) {
    let target;
    try { target = document.getElementById(decodeURIComponent(hash.slice(1))); } catch (_) { return; }
    if (!target) return;
    entries.forEach(entry => {
      if (entry.root.contains(target) || target.contains(entry.root)) load(entry);
    });
  }

  function intent(event) {
    if (!event.target.closest) return;
    if (event.type === 'click') {
      const link = event.target.closest('a[href^="#"]');
      if (link) loadAnchor(link.hash);
    }
    const entry = entries.find(item => item.root.contains(event.target));
    if (!entry || entry.state === 'ready') return;
    load(entry);
    if ((event.type === 'input' || event.type === 'change') && event.target.matches('[data-popup-field]')) {
      const field = event.target;
      entry.edits.set(field, { value: field.value, checked: field.checked, start: field.selectionStart ?? null, end: field.selectionEnd ?? null });
      event.stopImmediatePropagation();
    }
    // Preserve the first action after a fast scroll/hash jump, including custom
    // preview controls that become editable only when their controller mounts.
    const keyboard = event.type === 'keydown' && (event.key === 'Enter' || event.key === ' ');
    if ((event.type === 'click' || keyboard) && !event.target.closest('a[href]')) {
      const control = event.target.closest('button, [data-popup-inline], [data-popup-focus-field]');
      if (control && !control.disabled) {
        event.preventDefault();
        event.stopImmediatePropagation();
        entry.activation = control;
      }
    }
  }

  function observe() {
    pageActive = true;
    if (!view.IntersectionObserver) { entries.forEach(load); return; }
    if (!observer) observer = new view.IntersectionObserver(changes => {
      changes.forEach(change => {
        if (change.isIntersecting) load(entries.find(entry => entry.root === change.target));
      });
    }, { rootMargin: '320px 0px' });
    entries.filter(entry => entry.state !== 'ready').forEach(entry => observer.observe(entry.root));
    loadAnchor(view.location.hash);
  }
  const events = ['pointerover', 'pointerdown', 'focusin', 'input', 'change', 'keydown', 'click'];
  const onHash = () => loadAnchor(view.location.hash);
  const suspend = () => {
    pageActive = false;
    entries.forEach(entry => { entry.activation = null; });
    if (observer) observer.disconnect();
  };
  function cleanup() {
    if (observer) observer.disconnect();
    events.forEach(name => document.removeEventListener(name, intent, true));
    view.removeEventListener('hashchange', onHash);
    view.removeEventListener('pagehide', suspend);
    view.removeEventListener('pageshow', observe);
  }
  events.forEach(name => document.addEventListener(name, intent, true));
  view.addEventListener('hashchange', onHash);
  view.addEventListener('pagehide', suspend);
  view.addEventListener('pageshow', observe);
  observe();
  return { entries, cleanup };
});
